// NOTICE: KAPTION-INTEGRATION
// Store module for managing dashboard app notifications
// Supports individual notifications with custom action buttons

let notificationIdCounter = 0;

const generateNotificationId = () => {
  notificationIdCounter += 1;
  return `notif-${Date.now()}-${notificationIdCounter}`;
};

export const state = {
  // Individual notifications, keyed by notificationId
  // { notificationId: { id, appId, message, content, actions, timestamp } }
  notifications: {},
};

export const getters = {
  // Get all notifications
  getAllNotifications(_state) {
    return _state.notifications;
  },

  // Get notifications as sorted array (newest first)
  getNotificationsList(_state) {
    return Object.values(_state.notifications).sort(
      (a, b) => b.timestamp - a.timestamp
    );
  },

  // Get notifications for a specific app
  getNotificationsByAppId(_state) {
    return appId =>
      Object.values(_state.notifications)
        .filter(n => n.appId === appId)
        .sort((a, b) => b.timestamp - a.timestamp);
  },

  // Get notification by ID
  getNotificationById(_state) {
    return notificationId => _state.notifications[notificationId] || null;
  },

  // Get total notification count
  getTotalNotificationCount(_state) {
    return Object.keys(_state.notifications).length;
  },

  // Check if there are any notifications
  hasAnyNotifications(_state) {
    return Object.keys(_state.notifications).length > 0;
  },

  // Get count of notifications per app
  getNotificationCountByAppId(_state) {
    return appId =>
      Object.values(_state.notifications).filter(n => n.appId === appId).length;
  },

  // Get list of app IDs that have notifications
  getAppIdsWithNotifications(_state) {
    const appIds = new Set(
      Object.values(_state.notifications).map(n => n.appId)
    );
    return Array.from(appIds);
  },

  // BACKWARD COMPATIBILITY - Get total badge count (alias)
  getTotalBadgeCount(_state) {
    return Object.keys(_state.notifications).length;
  },

  // BACKWARD COMPATIBILITY - Check if there are any badges (alias)
  hasAnyBadges(_state) {
    return Object.keys(_state.notifications).length > 0;
  },
};

export const mutations = {
  // Add a new notification
  ADD_NOTIFICATION(_state, notification) {
    _state.notifications = {
      ..._state.notifications,
      [notification.id]: notification,
    };
  },

  // Remove a notification by ID
  REMOVE_NOTIFICATION(_state, notificationId) {
    const { [notificationId]: _, ...rest } = _state.notifications;
    _state.notifications = rest;
  },

  // Clear all notifications for an app
  CLEAR_APP_NOTIFICATIONS(_state, appId) {
    const filtered = {};
    Object.entries(_state.notifications).forEach(([id, notif]) => {
      if (notif.appId !== appId) {
        filtered[id] = notif;
      }
    });
    _state.notifications = filtered;
  },

  // Clear all notifications
  CLEAR_ALL_NOTIFICATIONS(_state) {
    _state.notifications = {};
  },
};

export const actions = {
  // Send a new notification from an app
  sendNotification(
    { commit },
    { appId, message, content, actions: notifActions, notificationId }
  ) {
    const id = notificationId || generateNotificationId();
    const notification = {
      id,
      appId,
      message,
      content: content || null,
      actions: notifActions || [],
      timestamp: Date.now(),
    };
    commit('ADD_NOTIFICATION', notification);
    return { success: true, notificationId: id };
  },

  // Dismiss a specific notification
  dismissNotification({ commit }, { notificationId }) {
    commit('REMOVE_NOTIFICATION', notificationId);
    return { success: true };
  },

  // Clear all notifications for an app (when tab is activated)
  clearAppNotifications({ commit }, { appId }) {
    commit('CLEAR_APP_NOTIFICATIONS', appId);
    return { success: true };
  },

  // Clear all notifications
  clearAllNotifications({ commit }) {
    commit('CLEAR_ALL_NOTIFICATIONS');
  },

  // BACKWARD COMPATIBILITY - setBadge creates a notification
  setBadge({ dispatch }, { appId, count, message, badgeActions }) {
    // Convert old badge format to notification
    return dispatch('sendNotification', {
      appId,
      message: message || `${count} notification${count !== 1 ? 's' : ''}`,
      content: { count },
      actions: badgeActions || [],
    });
  },

  // BACKWARD COMPATIBILITY - clearBadge clears all app notifications
  clearBadge({ dispatch }, { appId }) {
    return dispatch('clearAppNotifications', { appId });
  },

  // BACKWARD COMPATIBILITY - clearAllBadges clears all notifications
  clearAllBadges({ dispatch }) {
    return dispatch('clearAllNotifications');
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
